import moment from "moment-timezone";
import { IVenue } from "./venueSlice";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IFilterParameters, IReport } from "app/components/Dashboard/types";
import { IAverageSpendDay, IAverageSpendMonth, IAverageSpendPerPax, IAverageSpendPerDuration } from "app/services/reporting/reporting.type";

/** Initial state of the filter panel */
const initialFilterPanelState: IFilterParameters = {
  fromDate: moment(new Date().setDate(new Date().getDate() -7)).format('YYYY-MM-DD'),
  toDate: moment(new Date().setDate(new Date().getDate() + 1)).format('YYYY-MM-DD'),
  toDateDisplay: moment(new Date()).format('YYYY-MM-DD'),
  selectedReport:  {
    id: 0,
    urlParamText: "report",
    name: "Reporting Dashboard"
  },
  selectedVenue: { id: 0, name: '', isPosActive: false, timeZoneId: moment.tz.guess() },
  searchText: "",
  serviceGroupingPaxCountDate: moment(new Date()).format('YYYY-MM-DD'),
  isLoading: true,
  selectedVenues: [] as IVenue[],
  exportBtnClicked: false,
  isDirty: false
}

export interface DashboardState {
  filterParameters: IFilterParameters;
  avgWeeklySpendStats: IAverageSpendDay[];
  avgMonthlySpendStats: IAverageSpendMonth[];
  avgPerPaxSpendStats: IAverageSpendPerPax[];
  totalMonthlySpendStats: IAverageSpendMonth[];
  avgPerSpendDurationStats: IAverageSpendPerDuration[];
}

/** Initial state */
const initialState: DashboardState = {
  filterParameters: initialFilterPanelState,
  avgWeeklySpendStats: [] as IAverageSpendDay[],
  avgMonthlySpendStats: [] as IAverageSpendMonth[],
  avgPerPaxSpendStats: [] as IAverageSpendPerPax[],
  totalMonthlySpendStats: [] as IAverageSpendMonth[],
  avgPerSpendDurationStats: [] as IAverageSpendPerDuration[]
}

export const dashboardSlice = createSlice({
  name: 'Dashboard',
  initialState,
  reducers: {
    saveFromDate: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        filterParameters: {...state.filterParameters, fromDate: action.payload, isDirty: true}
      };
    },
    saveToDate: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        filterParameters: {...state.filterParameters, toDate: action.payload, isDirty: true}
      };
    },
    saveToDateDisplay: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        filterParameters: {...state.filterParameters, toDateDisplay: action.payload }
      };
    },
    saveSelectedVenue: (state, action: PayloadAction<IVenue>) => {
      return {
        ...state,
        filterParameters: {
          ...state.filterParameters, 
          selectedVenue: action.payload, 
          selectedVenues: [action.payload], 
          isDirty: true
        }
      };
    },
    saveMultipleSelectedVenues: (state, action: PayloadAction<IVenue[]>) => {
      return {
        ...state,
        filterParameters: {...state.filterParameters, selectedVenues: action.payload, isDirty: true }
      };
    },
    saveSelectedReport: (state, action: PayloadAction<IReport>) => {
      return {
        ...state,
        filterParameters: {...state.filterParameters, selectedReport: action.payload, isDirty: true}
      };
    },
    saveSearchText: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        filterParameters: {...state.filterParameters, searchText: action.payload, isDirty: true}
      };
    },
    saveAvgWeeklySpendStats: (state, action: PayloadAction<IAverageSpendDay[]>) => {
      return {
        ...state,
        avgWeeklySpendStats: action.payload
      };
    },
    saveAvgMonthlySpendStats: (state, action: PayloadAction<IAverageSpendMonth[]>) => {
      return {
        ...state,
        avgMonthlySpendStats: action.payload
      };
    },
    saveTotalMonthlySpendStats: (state, action: PayloadAction<IAverageSpendMonth[]>) => {
      return {
        ...state,
        totalMonthlySpendStats: action.payload
      };
    },
    saveAvgPerPaxSpendStats: (state, action: PayloadAction<IAverageSpendPerPax[]>) => {
      return {
        ...state,
        avgPerPaxSpendStats: action.payload
      };
    },
    saveAvgPerSpendDurationStats: (state, action: PayloadAction<IAverageSpendPerDuration[]>) => {
      return {
        ...state,
        avgPerSpendDurationStats: action.payload
      };
    },
    setBookingServiceGroupPaxDate: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        filterParameters: { ...state.filterParameters, serviceGroupingPaxCountDate: action.payload, isDirty: true }
      };
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        filterParameters: { ...state.filterParameters, isLoading: action.payload }
      };
      },
    setExportBtnClicked: (state, action: PayloadAction<boolean>) => {
        return {
            ...state,
            filterParameters: {...state.filterParameters, exportBtnClicked: action.payload }
        };
    },
    setIsDirty: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        filterParameters: {...state.filterParameters, isDirty: action.payload}
      }
    }
  }
});

export const { 
  saveAvgWeeklySpendStats, 
  saveAvgMonthlySpendStats, 
  saveTotalMonthlySpendStats, 
  saveAvgPerPaxSpendStats, 
  saveAvgPerSpendDurationStats, 
  saveFromDate, 
  saveToDate, 
  saveToDateDisplay, 
  saveSelectedVenue, 
  saveMultipleSelectedVenues,
  saveSelectedReport, 
  saveSearchText, 
  setBookingServiceGroupPaxDate,
  setIsLoading,
  setExportBtnClicked,
  setIsDirty } = dashboardSlice.actions;

export default dashboardSlice.reducer;
